import { Checkbox } from "@fluentui/react-components";
import { ChevronRight12Regular, Filter16Regular } from "@fluentui/react-icons";
import { useLocation } from "react-router";
import { useState } from "react";

import { isInternalUser } from "common/MetaActions";
import ContextualMenuV2 from "components/contextualMenu/ContextualMenuV2";
import { InboxFiltersEnum, TranslatedCategories } from "models/inbox/inbox";
import { useRootStore } from "store";
import { useGlobalContext } from "app/store/GlobalContext";
import { useInboxReplyContext } from "app/store/InboxReplyContext";
import UnsavedChangesModal from "components/shared/unsavedChangesModal/UnsavedChangesModal";

export default function InboxFiltersActions() {
  const [showModal, setShowModal] = useState(false);
  const [pendingAction, setPendingAction] = useState<(() => void) | null>(null);
  
  const { isReplying, isDirty, setIsReplying, clearDirty } = useInboxReplyContext();
  const shouldShowModal = isReplying && isDirty;
  const { handleNavigation } = useGlobalContext();
  const { workingCustomerId } = useRootStore((state) => state.global);

  const handleModalConfirm = () => {
    if (pendingAction) {
      setIsReplying(false);
      clearDirty();
      pendingAction();
    }
    setShowModal(false);
    setPendingAction(null);
  };

  const handleModalCancel = () => {
    setShowModal(false);
    setPendingAction(null);
  };

  const executeFilterAction = (action: () => void) => {
    if (shouldShowModal) {
      setPendingAction(() => action);
      setShowModal(true);
    } else {
      action();
    }
  };

  const { isLoadingMyThreads, isLoadingAllThreads, isLoadingSentMessages } =
    useRootStore((state) => state.inbox);

  const { archive } = useRootStore((state) => state);
  const { isLoadingArchiveRoot } = archive;

  const isLoading =
    isLoadingMyThreads ||
    isLoadingAllThreads ||
    isLoadingSentMessages ||
    isLoadingArchiveRoot;

  const location = useLocation();

  const {
    fetchMyThreads,
    fetchAllThreads,
    sentMessagesSearchTerm,
    setActiveFilters,
    activeFilters
  } = useRootStore((state) => state.inbox);

  const selectedCategories = activeFilters.categories;

  const updateThreadsWithFilters = (filters: typeof activeFilters) => {
    const redirectURL =
      location.pathname === "/inbox/my/new" ? location.pathname : "/inbox/my";
    handleNavigation({
      url: redirectURL,
      eventTitle: "Inbox_my"
    });

    setActiveFilters(filters);
    fetchMyThreads(workingCustomerId, 0, sentMessagesSearchTerm);
    fetchAllThreads(workingCustomerId, 0, sentMessagesSearchTerm);
  };

  const handleToggleFilter = (filterName: keyof typeof activeFilters) => {
    const toggleAction = () => {
      const newFilters = {
        ...activeFilters,
        [filterName]: !activeFilters[filterName]
      };
      updateThreadsWithFilters(newFilters);
    };
    
    executeFilterAction(toggleAction);
  };

  const handleToggleCategory = (category: string) => {
    const toggleAction = () => {
      let newCategories;
      if (activeFilters.categories.includes(category)) {
        newCategories = activeFilters.categories.filter((c) => c !== category);
      } else {
        newCategories = [...activeFilters.categories, category];
      }
      updateThreadsWithFilters({
        ...activeFilters,
        categories: newCategories
      });
    };
    
    executeFilterAction(toggleAction);
  };

  // This const is added in order to hide menu items
  // while their functionality is not implemented on the BE side
  // it is passed as a value in the hidden property of each item in filterItems
  // remove it as filters get implemented
  const isNotYetImplemented = true;

  const filterItems = [
    {
      key: "categories",
      hidden: !isInternalUser() || isNotYetImplemented,
      submenu: {
        buttonStyle: "p-0",
        buttonIcon: () => (
          <div className="d-flex align-items-center">
            <Checkbox
              checked={selectedCategories.length > 0}
              label="Kategori"
              className="disable-click"
            />
            <ChevronRight12Regular />
          </div>
        ),
        submenuItems: [
          {
            key: "categories-payroll",
            content: (
              <div>
                <Checkbox
                  label={TranslatedCategories.payroll}
                  className="disable-click"
                  checked={selectedCategories.includes("payroll")}
                />
              </div>
            ),
            onClick: () => handleToggleCategory("payroll"),
            persistOnClick: true
          },
          {
            key: "categories-accounting",
            content: (
              <div>
                <Checkbox
                  label={TranslatedCategories.accounting}
                  className="disable-click"
                  checked={selectedCategories.includes("accounting")}
                />
              </div>
            ),
            onClick: () => handleToggleCategory("accounting"),
            persistOnClick: true
          },
          {
            key: "categories-general",
            content: (
              <div>
                <Checkbox
                  label={TranslatedCategories.general}
                  className="disable-click"
                  checked={selectedCategories.includes("general")}
                />
              </div>
            ),
            onClick: () => handleToggleCategory("general"),
            persistOnClick: true
          },
          {
            key: "categories-reports",
            content: (
              <div>
                <Checkbox
                  label={TranslatedCategories.reports}
                  className="disable-click"
                  checked={selectedCategories.includes("reports")}
                />
              </div>
            ),
            onClick: () => handleToggleCategory("reports"),
            persistOnClick: true
          },
          {
            key: "categories-uncategorised",
            content: (
              <div>
                <Checkbox
                  label={TranslatedCategories.uncategorised}
                  className="disable-click"
                  checked={selectedCategories.includes("uncategorised")}
                />
              </div>
            ),
            onClick: () => handleToggleCategory("uncategorised"),
            persistOnClick: true
          }
        ]
      }
    },
    {
      key: "flagged",
      content: (
        <div>
          <Checkbox
            label="Flaggat"
            checked={activeFilters.flagged}
            className="disable-click"
          />
        </div>
      ),
      onClick: () => handleToggleFilter(InboxFiltersEnum.FLAGGED),
      persistOnClick: true,
      hidden: isNotYetImplemented
    },
    {
      key: "unread",
      content: (
        <div>
          <Checkbox
            label="Oläst"
            checked={activeFilters.unread}
            className="disable-click"
          />
        </div>
      ),
      onClick: (e) => handleToggleFilter(InboxFiltersEnum.UNREAD),
      persistOnClick: true,
      hidden: isNotYetImplemented
    },
    {
      key: "drafts",
      content: (
        <div>
          <Checkbox
            label="Utkast"
            checked={activeFilters.drafts}
            className="disable-click"
          />
        </div>
      ),
      onClick: () => handleToggleFilter(InboxFiltersEnum.DRAFTS),
      persistOnClick: true,
      hidden: isNotYetImplemented
    },
    {
      key: "archived",
      content: (
        <div>
          <Checkbox
            label="Arkiverade"
            checked={activeFilters.archived}
            className="disable-click"
          />
        </div>
      ),
      onClick: () => handleToggleFilter(InboxFiltersEnum.ARCHIVED),
      persistOnClick: true,
      hidden: !isInternalUser()
    }
  ];

  return (
    <>
      <ContextualMenuV2
        menuItems={filterItems}
        buttonIcon={() => <Filter16Regular />}
        buttonStyle="btn btn-round btn-neutral btn-sm btn-outline mr-2"
        buttonAppearance="transparent"
        buttonDisabled={isLoading}
      />
      
      {showModal && (
        <UnsavedChangesModal
          text="Du har ändringar som ej sparats i ditt svar. Är du säker på att du vill filtrera och lämna utan att spara?"
          onConfirm={handleModalConfirm}
          onCancel={handleModalCancel}
          onDismiss={handleModalCancel}
        />
      )}
    </>
  );
}
